// 공개 사고사례 탐색기 — 페이지 전용 KO/EN 문자열(전역 locale 미오염). i18n.language 로 선택.
export type AcLang = 'ko' | 'en'

export interface AcStrings {
  metaTitle: string
  metaDesc: string
  badge: string
  heroTitle: string
  heroHighlight: string
  heroSubtitle: string
  heroStat: string
  searchPlaceholder: string
  searchBtn: string
  examplesLabel: string
  examples: string[]
  searching: string
  relatedTitle: (label: string) => string
  relatedSub: string
  distTitle: string
  byType: string
  byCause: string
  casesTitle: string
  hazardLabel: string
  preventionLabel: string
  sourceLabel: string
  matchLabel: string
  emptyTitle: string
  emptyDesc: string
  gateBadge: string
  gateTitle: string
  gateDesc: (label: string) => string
  gateBullets: string[]
  gateCta: string
  sampleLock: string
  sampleCaption: string
  raSampleTitle: string
  raCols: string[]
  disclaimerTitle: string
  diagTitle: string
  diagLead: (pct: number) => string
  moreTitle: (label: string) => string
  moreCta: string
}

export const AC_I18N: Record<AcLang, AcStrings> = {
  ko: {
    metaTitle: '산업재해 사고사례 검색 | Heinrich AC DataCenter',
    metaDesc: '작업만 입력하면 실제 산업재해 사고사례를 AI가 정제해 보여줍니다. 무료·회원가입 없이 이용하세요.',
    badge: 'AI 정제 · 실제 사고사례',
    heroTitle: '진행할 작업에서 실제로 발생한',
    heroHighlight: '산업재해 사고사례',
    heroSubtitle: '작업을 입력하면 유사한 실제 사고를 AI가 읽기 쉽게 정리해 보여드립니다. 로그인·결제 없이 바로.',
    heroStat: '270만+ 실제 산업재해 사고사례',
    searchPlaceholder: '예: 지게차로 자재 하역, 고소 비계 작업, 컨베이어 점검…',
    searchBtn: '사고사례 검색',
    examplesLabel: '이렇게 검색해 보세요',
    examples: ['지게차 하역 작업', '고소 비계 해체', '밀폐공간 배관 용접', '프레스 금형 교체', '크레인 인양 작업'],
    searching: '유사 사고사례를 찾고 정제하는 중…',
    relatedTitle: (label) => `이 작업과 유사한 사고 ${label}건`,
    relatedSub: '실제 발생한 산업재해를 기반으로 합니다.',
    distTitle: '이 작업의 사고 경향',
    byType: '사고유형 분포',
    byCause: '주요 기인물',
    casesTitle: '관련 사고사례',
    hazardLabel: '핵심 위험요인',
    preventionLabel: '예방 포인트',
    sourceLabel: '출처',
    matchLabel: '유사도',
    emptyTitle: '검색 결과가 없습니다',
    emptyDesc: '작업을 조금 더 구체적으로 입력해 보세요. (예: 설비·작업 방식 포함)',
    gateBadge: '가입하면 바로',
    gateTitle: '이 사고들을 위험성평가로 바꿔드립니다',
    gateDesc: (label) => `관련 사고 ${label}건 전체 열람과, 이 작업에 맞춘 위험성평가·JSA·감소대책 자동 생성은 무료 회원가입 후 이용할 수 있습니다.`,
    gateBullets: [
      '실제 사고 기반 위험요인 망라 도출',
      'KOSHA 5×5 매트릭스 위험성평가표 자동 작성',
      'JSA·TBM·작업허가서 연계 + PDF 산출물',
    ],
    gateCta: '무료로 위험성평가 생성하기',
    sampleLock: '가입 후 열람',
    sampleCaption: '↑ 가입하면 내 작업에 맞춰 이렇게 생성됩니다',
    raSampleTitle: '위험성평가표 (예시)',
    raCols: ['단위작업', '위험요인', '빈도', '강도', '감소대책'],
    disclaimerTitle: '안내',
    diagTitle: '이 작업의 핵심 위험 진단',
    diagLead: (pct) => `관련 실제 사고의 약 ${pct}%가 아래 유형에 집중됩니다.`,
    moreTitle: (label) => `이 작업과 관련된 사고 ${label}건`,
    moreCta: '전체 사례 열람하기',
  },
  en: {
    metaTitle: 'Workplace Accident Case Search | Heinrich AC DataCenter',
    metaDesc: 'Enter a task and AI shows real, cleaned workplace-accident cases. Free, no sign-up required.',
    badge: 'AI-cleaned · Real accidents',
    heroTitle: 'Real workplace accidents',
    heroHighlight: 'that happened in your work',
    heroSubtitle: 'Type a task and AI surfaces similar real accidents, cleaned and easy to read. No login, no payment.',
    heroStat: '2.7M+ real workplace accident records',
    searchPlaceholder: 'e.g. forklift unloading, scaffold work at height, conveyor maintenance…',
    searchBtn: 'Search accidents',
    examplesLabel: 'Try searching',
    examples: ['forklift unloading', 'scaffold dismantling at height', 'confined-space pipe welding', 'press die change', 'crane lifting'],
    searching: 'Finding and cleaning similar accidents…',
    relatedTitle: (label) => `${label} similar accidents for this task`,
    relatedSub: 'Based on real, recorded workplace accidents.',
    distTitle: 'Accident patterns for this task',
    byType: 'By accident type',
    byCause: 'Top agents/causes',
    casesTitle: 'Related accident cases',
    hazardLabel: 'Key hazard',
    preventionLabel: 'Prevention',
    sourceLabel: 'Source',
    matchLabel: 'Match',
    emptyTitle: 'No results',
    emptyDesc: 'Try describing the task more specifically (include equipment / method).',
    gateBadge: 'After sign-up',
    gateTitle: 'Turn these accidents into a risk assessment',
    gateDesc: (label) => `View all ${label} related accidents and auto-generate a risk assessment, JSA and controls tailored to your task — free after sign-up.`,
    gateBullets: [
      'Comprehensive hazard identification from real accidents',
      'Auto-filled KOSHA 5×5 risk-assessment matrix',
      'JSA / TBM / work-permit linkage + PDF outputs',
    ],
    gateCta: 'Generate a risk assessment free',
    sampleLock: 'Unlock after sign-up',
    sampleCaption: '↑ After sign-up it is generated for your exact task',
    raSampleTitle: 'Risk Assessment (sample)',
    raCols: ['Unit task', 'Hazard', 'Freq.', 'Sev.', 'Control'],
    disclaimerTitle: 'Notice',
    diagTitle: 'Key risk diagnosis for this task',
    diagLead: (pct) => `About ${pct}% of related real accidents concentrate in these types.`,
    moreTitle: (label) => `${label} accidents related to this task`,
    moreCta: 'View all cases',
  },
}
